import React, { ChangeEvent, useEffect, useState } from "react";
import { Box, Checkbox, Grid, GridItem } from "@chakra-ui/react";
import { sortStudentsAlphabetically } from "./utils";
import { colours } from "@/utils/themeUtils";

export interface SelectableStudent {
	id: string;
	label: string;
}

interface Props {
	students: SelectableStudent[];
	handleSelectionChange: (selectedStudentIds: string[]) => void;
	/**
	 * All students are selected by default. If this prop is provided, only the students with the given IDs will be selected.
	 */
	preselectedStudentIds?: string[];
}

export function StudentCheckboxSelector(props: Props): JSX.Element {
	function getStudentsToAutoSelect(): string[] {
		if (props.preselectedStudentIds && props.preselectedStudentIds?.length >= 1) {
			return props.preselectedStudentIds;
		}
		return props.students.map((student) => student.id);
	}

	const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>(getStudentsToAutoSelect());

	// Whenever selectedStudentIds changes (including mount), call handleSelectionChange so parent can use the updated selection
	useEffect(() => {
		props.handleSelectionChange(selectedStudentIds);
	}, [props, selectedStudentIds]);

	function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>): void {
		const studentId = event.target.id;

		const updatedSelection = selectedStudentIds.includes(studentId)
			? selectedStudentIds.filter((id) => id !== studentId)
			: [...selectedStudentIds, studentId];

		setSelectedStudentIds(updatedSelection);
	}

	function handleSelectAllChange(event: ChangeEvent<HTMLInputElement>): void {
		if (event.target.checked) {
			const allIds = props.students.map((student) => student.id);
			setSelectedStudentIds(allIds);
		} else {
			setSelectedStudentIds([]);
		}
	}

	const COLUMN_COUNT = 2;

	function getRows(items: SelectableStudent[]): SelectableStudent[][] {
		const sortedItems = sortStudentsAlphabetically(items);

		const rows: SelectableStudent[][] = [];
		for (let i = 0; i < sortedItems.length; i += COLUMN_COUNT) {
			rows.push(sortedItems.slice(i, i + COLUMN_COUNT));
		}
		return rows;
	}

	const rows = getRows(props.students);

	// Determine the state of the "All Students" checkbox
	const isAllSelected = selectedStudentIds.length === props.students.length;
	const isIndeterminate = !isAllSelected && selectedStudentIds.length > 0;

	return (
		<Box>
			<Checkbox
				isIndeterminate={isIndeterminate}
				isChecked={isAllSelected}
				onChange={handleSelectAllChange}
				spacing={4}
				fontWeight={500}
				pb={5}
				data-testid="select-all-students-checkbox"
			>
				All students
			</Checkbox>
			<Box border={"1px solid #EBECF0"} rounded={"md"}>
				{rows.map((gridrow, index) => (
					<Box key={index} _odd={{ bg: `${colours.neutrals[50]}99` }}>
						<Grid templateColumns={`repeat(${COLUMN_COUNT}, 1fr)`}>
							{gridrow.map((student, index) => (
								<GridItem key={index} p={4}>
									<Checkbox
										isChecked={selectedStudentIds.includes(student.id)}
										id={student.id}
										onChange={handleCheckboxChange}
										spacing={3}
										maxWidth="200px"
									>
										{student.label}
									</Checkbox>
								</GridItem>
							))}
						</Grid>
					</Box>
				))}
			</Box>
		</Box>
	);
}
